import { Badge, Button, Card, CardContent, Spinner } from '@crm/ui';
import { HandCoins, Pencil, Percent, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog, EmptyState, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  COMMISSIONS_ROUTES,
  type CommissionRule,
  useCommissionRules,
  useDeleteCommissionRule,
} from '../api';
import { RuleDialog } from '../components/rule-dialog';

/** A regra em uma frase: para quem e sobre o que ela vale. */
function describe(rule: CommissionRule): string {
  return [
    rule.professionalName ?? 'Todos os profissionais',
    rule.serviceName ?? 'Todos os servicos',
  ].join(' - ');
}

/** Quantas regras entram na tela por vez. */
const PAGINA = 20;

export function CommissionRulesPage() {
  useDocumentTitle('Regras de comissao');

  const [visiveis, setVisiveis] = useState(PAGINA);
  const navigate = useNavigate();
  const canWrite = useCan('finance', 'WRITE');

  const rules = useCommissionRules();
  const deleteRule = useDeleteCommissionRule();

  const [editing, setEditing] = useState<CommissionRule | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<CommissionRule | null>(null);

  function openDialog(rule: CommissionRule | null) {
    setEditing(rule);
    setDialogOpen(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Percent />}
        title="Regras de comissao"
        description="Quanto cada profissional ganha sobre o que atende."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(COMMISSIONS_ROUTES.REPORT)}>
              Ver apuracao
            </Button>
            {canWrite ? (
              <Button onClick={() => openDialog(null)}>
                <Plus className="h-4 w-4" />
                <span>
                  Nova<span className="hidden sm:inline"> regra</span>
                </span>
              </Button>
            ) : null}
          </div>
        }
      />

      {deleteRule.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(deleteRule.error)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="space-y-4">
          {rules.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : rules.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(rules.error)}</p>
          ) : rules.data && rules.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {/* Um lote por vez: com regra por profissional e por servico a
                  lista cresce sem teto. */}
              {rules.data.data.slice(0, visiveis).map((rule) => (
                <li
                  key={rule.id}
                  className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{rule.professionalName ?? 'Regra da casa'}</p>
                      <Badge variant="secondary">{rule.percent}% de comissao</Badge>
                      {rule.isActive ? null : <Badge variant="outline">Inativa</Badge>}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{describe(rule)}</p>
                  </div>

                  {canWrite ? (
                    <div className="flex items-center gap-1 sm:justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Editar"
                        onClick={() => openDialog(rule)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Excluir"
                        onClick={() => setPendingRemoval(rule)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<HandCoins />} title="Nenhuma regra de comissao" />
          )}

          {(rules.data?.data.length ?? 0) > visiveis ? (
            <div className="flex flex-col items-center gap-1">
              <Button
                variant="ghost"
                className="w-full text-primary sm:w-auto"
                onClick={() => setVisiveis((atual) => atual + PAGINA)}
              >
                <Plus className="h-4 w-4" />
                Mostrar mais
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <RuleDialog
        open={isDialogOpen}
        rule={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir regra"
        message="A regra para de valer na proxima apuracao. O que ja foi pago fica no historico."
        loading={deleteRule.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteRule.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
